import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Mail, Phone } from "lucide-react";

const LOGO_SVG =
  "https://customer-assets.emergentagent.com/job_ivory-atelier/artifacts/vnon5z32_t%26s%20ivory%20logo.svg";

/**
 * Minimal launch nav — centered T&S IVORY SVG logo + a single right-aligned "Join" link
 * that scrolls to #join on the landing page (or routes home + scrolls when on other pages).
 */
export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHero = pathname === "/" && !scrolled;
  const textColor = onHero ? "#F7F4EF" : "#2C2724";
  // SVG artwork is dark — invert to near-white ivory when over hero video, keep dark on ivory background.
  const logoFilter = onHero
    ? "brightness(0) invert(1) brightness(0.97) sepia(0.06)"
    : "none";

  const handleJoin = (e) => {
    if (pathname === "/") {
      e.preventDefault();
      document.getElementById("join")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="primary-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#F5F2ED]/95 backdrop-blur-sm border-b border-[#EEE7DE]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-14 py-5 grid grid-cols-3 items-center">
        <div /> {/* left spacer */}
        <Link to="/" data-testid="nav-home-logo" className="flex items-center justify-center">
          <img
            src={LOGO_SVG}
            alt="T&S Ivory"
            data-testid="nav-logo-img"
            className="h-9 md:h-11 w-auto"
            style={{ filter: logoFilter, transition: "filter 500ms ease" }}
          />
        </Link>
        <div className="flex justify-end">
          <Link
            to="/#join"
            onClick={handleJoin}
            data-testid="nav-join"
            className="text-[13px] tracking-[0.36em] uppercase font-medium relative inline-block transition-colors duration-300 after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-[1px] after:bg-current after:origin-center after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300"
            style={{ color: textColor }}
          >
            Join
          </Link>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#4A3E39] text-[#EEE7DE] pt-11 pb-5 px-6 lg:px-14 relative overflow-hidden" data-testid="site-footer">
      <div className="max-w-[1200px] mx-auto">
        {/* Editorial two-column block — Address + Contact */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-8 lg:gap-12">
          {/* Visit */}
          <div className="md:col-span-6">
            <p className="eyebrow text-[#D7CABC] mb-4">Visit</p>
            <p className="font-display text-xl md:text-[22px] leading-snug text-[#F5F2ED]">
              Ganesh Chowk
              <br />
              Charkop, Kandivali (W)
              <br />
              Mumbai – 400067
            </p>
            <p className="mt-4 text-[11px] tracking-[0.32em] uppercase text-[#D7CABC]">
              Opening 2026
            </p>
          </div>

          {/* Contact */}
          <div className="md:col-span-6">
            <p className="eyebrow text-[#D7CABC] mb-4">Contact</p>
            <ul className="space-y-2.5 font-display text-lg md:text-xl leading-snug">
              <li>
                <a
                  href="https://instagram.com/tnsivory"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-instagram"
                  className="inline-flex items-center gap-3 text-[#F5F2ED] hover:text-white transition-colors"
                >
                  <Instagram size={16} strokeWidth={1.25} className="opacity-90" />
                  <span>@tnsivory</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:hello@tsivory.com"
                  data-testid="footer-email"
                  className="inline-flex items-center gap-3 text-[#F5F2ED] hover:text-white transition-colors"
                >
                  <Mail size={16} strokeWidth={1.25} className="opacity-90" />
                  <span>hello@tsivory.com</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+918169084596"
                  data-testid="footer-phone"
                  className="inline-flex items-center gap-3 text-[#F5F2ED] hover:text-white transition-colors"
                >
                  <Phone size={16} strokeWidth={1.25} className="opacity-90" />
                  <span>+91 81690 84596 — Kamal</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Oversized logo SVG */}
        <div className="mt-9 lg:mt-10 -mb-1 flex justify-start">
          <img
            src={LOGO_SVG}
            alt="T&S Ivory"
            data-testid="footer-logo-img"
            className="w-full max-w-[600px] h-auto"
            style={{
              filter:
                "brightness(0) invert(96%) sepia(8%) saturate(186%) hue-rotate(353deg) brightness(101%) contrast(95%)",
            }}
          />
        </div>

        <div className="ivory-divider opacity-25 mt-4 mb-3" />

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[10px] tracking-[0.32em] uppercase text-[#D7CABC]">
          <p>© {new Date().getFullYear()} T&amp;S Ivory · Hair · Beauty · You</p>
          <div className="flex gap-8">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <>
      <Nav />
      <main className="min-h-screen">{children}</main>
      <Footer />
    </>
  );
}
